/**
 * ICF Guide actions - display ICF prompt list and fetch ICF article content
 */
import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import _uniqBy from 'lodash/uniqBy';
import * as types from '../constants/ActionTypes';

import { getICFAccessdArticle } from './action-icf-page';
import { elmoExperimentResourceNames, elmoTreatmentNames } from '../../lib/experiments-helper';
import { getElmoExperiments } from './action-app';

const BNA_CHANNEL = 'BNA';

const getBNAArticles = recommendArticles => Array.isArray(recommendArticles) && recommendArticles.filter(article => article.channel === BNA_CHANNEL);

/* --------------- Actions --------------- */
// Prepare fetching ICF content request
const requestICFContents = url => ({ type: types.REQUEST_ICF_CONTENTS, payload: url });

const receivedICFContents = payload => ({ type: types.RECEIVED_ICF_CONTENTS, payload });

const receviedICFContentsError = () => ({ type: types.RECEIVED_ICF_CONTENTS_ERROR });

// Parse BNA-ICF remove config from String to Object type; ICF remote config example: `ICF1:BNA-1|BNA-2`
const getRemoteData = (remoteData) => {
  if (!remoteData || typeof remoteData !== 'string') {
    return null;
  }
  const remoteList = remoteData.split(',');
  const response = [];
  remoteList.forEach((map) => {
    const tmp = map.split(':');

    if (Array.isArray(tmp) && tmp.length && typeof tmp[1] === 'string') {
      const temp = { icf: tmp[0], bnaList: tmp[1].split('|') };
      response.push(temp);
    }
  });

  return response;
};

const assignTreatment = () => ({
  type: types.ICF_CONFIG_CHECKER,
  payload: elmoTreatmentNames.treatmentForIcfDefault,
});

/**
 * Use BNA ID to get ICF ID from remote config mapping
 * @param  {Array}  remoteData  remote config data [{ icf: ICFID, bnaList: [...BNAID] }, ...].
 * @param  {String} bnaId       BNA ID.
 * @return {String}             Return ICF id
 */
const getICFId = (remoteData, bnaId) => {
  if (!Array.isArray(remoteData) || !remoteData.length) {
    return null;
  }

  const icfContent = _find(remoteData, map => Array.isArray(map.bnaList) && map.bnaList.indexOf(bnaId) >= 0);
  return icfContent ? icfContent.icf : null;
};

// Get ICF id from BNA-ICF mapping stored in the Remote config
const getICFList = (remoteData, recommendArticles) => {
  const bnaArticles = getBNAArticles(recommendArticles);
  const bnaMapping = getRemoteData(remoteData);

  const response = [];
  if (!Array.isArray(bnaArticles) || !bnaArticles.length || _isEmpty(bnaMapping)) {
    return response;
  }
  bnaArticles.forEach((article) => {
    const bnaId = (article.channelDetails || {}).bnaId;
    const icfId = getICFId(bnaMapping, bnaId);

    if (icfId) {
      response.push(icfId);
    }
  });
  return _uniqBy(response); // remove ICF duplications
};

// Formate URL with single/multiple ICF id
const getURL = (ICFList) => {
  if (!Array.isArray(ICFList) || !ICFList.length) {
    return '';
  }
  let baseUrl = '';
  ICFList.forEach((id) => {
    baseUrl += baseUrl ? `&icf_id=${id}` : `icf_id=${id}`;
  });
  return baseUrl;
};

// Using URL to fetch ICF contents
const fetchGuideContents = query => (dispatch) => {
  dispatch(requestICFContents(query));

  return axios.get(`/smarthelp/interactive-guide?${query}`)
    .then((response) => {
      dispatch(receivedICFContents(response.data));
      // Getting the ICF Visited List
      dispatch(getICFAccessdArticle());
      // Elmo experiement for ICF Prompt
      dispatch(getElmoExperiments(elmoExperimentResourceNames.icfPrompt));
    })
    .catch(() => {
      dispatch(receviedICFContentsError());
    });
};

const fetchICFContents = ({ dispatch, remoteData, recommendArticles }) => {
  if (typeof dispatch !== 'function' || !remoteData || !Array.isArray(recommendArticles)) {
    return null;
  }
  dispatch(assignTreatment());

  const ICFList = getICFList(remoteData, recommendArticles); // using remote config (BNA-ICF mapping) to get ICF id
  if (!Array.isArray(ICFList) || !ICFList.length) {
    return null;
  }

  const fetchingQuery = getURL(ICFList);

  return dispatch(fetchGuideContents(fetchingQuery));
};

module.exports = {
  fetchICFContents,
};
